import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useEffect, useState } from "react";
import { getEmployeesByAdminAndOrg } from "services/getEmployees";
import { createLegalAdvisoryProsecutorAssignment, createLegalAdvisoryRemark, createLegalRespondbackFeedback, updateLegalAdvisory, updateLegalAdvisoryProsecutorAssignment } from "services/legalAdvisoryService";
import { createLegislativeDraftingRemark, createLegislativeProsecutorAssignment, createLegislativeRespondbackFeedback, updateLegislativeDrafting, updateLegislativeProsecutorAssignment } from "services/legislativeService";
import { createSectorDirectiveProsecutorAssignment, createSectorDirectiveRemark, createSectorDirectiveRespondbackFeedback, updateSectorDirective, updateSectorDirectiveProsecutorAssignment } from "services/sectorDirectives";
import { createLegalTrainingProvisionProsecutorAssignment, createLegalTrainingProvisionRemark, createLegalTrainingProvisionRespondbackFeedback, updateLegalTrainingProvision, updateLegalTrainingProvisionProsecutorAssignment } from "services/legalTrainingProvision";
import { LoadingIcon } from "utils/constant";
import { FeedbackAllowedRoles, LEGAL_TRAINING_PROVISION_REQUEST_STATUS, LEGISLATIVE_DRAFTING_REQUEST_STATUS } from "utils/constantValues";
import { getUserType } from "utils/helpers";
import { useTranslation } from "react-i18next";

interface TeamMember {
    id: number;
    name: string;
    role: string;
    isTeamLeader: boolean;
    prosecutor_name?: string;
    is_team_leader?: boolean;
}

// Helper function to generate initials
const getInitials = (name: string) => {
    if (!name) return "";
    const parts = name.split(" ");
    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }
    return (
        parts[0].charAt(0).toUpperCase() +
        parts[parts.length - 1].charAt(0).toUpperCase()
    );
};

export default function DirectorRemark({ setEdit, setFormState, formState, type }: { setEdit: any, setFormState: any, formState: any, type: string }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const { showToast } = useToast();
    const contextData = useAuth();
    const [selectedProsecutor, setSelectedProsecutor] = useState<TeamMember | null>(null);
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
    const [employees, setEmployees] = useState([]);
    const [validationError, setValidationError] = useState<string | null>(null);

    const bearerToken = contextData.token ?? contextData?.user?.access_token ?? contextData?.user.user?.access_token;
    const adminUnit = contextData?.user?.admin_unit || "";
    const orgUnit = contextData?.user?.org_unit || "";

    useEffect(() => {
        let cancelled = false;
        getEmployeesByAdminAndOrg({
            bearer: bearerToken,
            org_unit: orgUnit,
            admin_unit: adminUnit,
        })
            .then((data) => {
                if (!cancelled) {
                    setEmployees(
                        data
                            .filter((emp: any) =>
                                emp.roles?.some((role: string) => role === "LegalResearchProsecutor")
                            )
                            .map((emp: any) => ({
                                id: emp.user.id,
                                name: emp.user.first_name,
                                role: emp.roles[0] || "LegalResearchProsecutor",
                                isTeamLeader: false,
                            }))
                    );
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    console.error("Error fetching:", error);
                }
            });
        return () => { cancelled = true; };
    }, [bearerToken, adminUnit, orgUnit]);

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setFormState((prev: any) => ({
            ...prev,
            [name]: value,
        }));
        console.log("check remark", formState.remark);
    };

    const handleReject = async () => {
        setLoading(true);
        const remarkData = new FormData();
        if (type === "legal")
            remarkData.append("advisory_request", formState.id);
        else if (type === "sector_directives")
            remarkData.append("request", formState.id);
        else if (type === "training")
            remarkData.append("training_request", formState.id);
        else
            remarkData.append("instruction", formState.id);
        const directorRole = contextData?.user?.roles.find((role: string) => role.toLowerCase().includes("director"));
        const feedbackRole = directorRole || contextData?.user?.roles.find((role: string) => FeedbackAllowedRoles.has(role)) || contextData.user?.roles[0];
        remarkData.append("role", getUserType(feedbackRole));
        remarkData.append("summary", formState.remark);
        remarkData.append("feedback_type", "Review");
        remarkData.append("given_by", contextData?.user?.full_name || "");

        try {
            let remark_response;
            if (type === "legal")
                remark_response = await createLegalRespondbackFeedback(remarkData);
            else if (type === "sector_directives")
                remark_response = await createSectorDirectiveRespondbackFeedback(remarkData);
            else if (type === "training")
                remark_response = await createLegalTrainingProvisionRespondbackFeedback(remarkData);
            else
                remark_response = await createLegislativeRespondbackFeedback(remarkData);
            console.log(remark_response);
            const data = new FormData();
            data.append("current_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.back_to_head.value);
            let response: any;
            if (type === "legal") {
                data.append("status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.back_to_head.value);
                response = await updateLegalAdvisory(formState.id, data);
            }
            else if (type === "sector_directives") {
                data.append("status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.back_to_head.value);
                response = await updateSectorDirective(formState.id, data);
            }
            else if (type === "training") {
                data.append("status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.back_to_head.value);
                response = await updateLegalTrainingProvision(formState.id, data);
            }
            else {
                data.append("current_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.back_to_head.value);
                response = await updateLegislativeDrafting(formState.id, data);
            }

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("FeedbackSubmittedSuccess") || "Feedback submitted successfully!", "success");
            setEdit(false);
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateRequest") || "Failed to update request.", "error");
        } finally {
            setLoading(false);
        }
    }

    const handleForwardToCoordinator = async () => {
        setLoading(true);
        const remarkData = new FormData();
        if (type === "legal")
            remarkData.append("advisory_request", formState.id);
        else if (type === "sector_directives")
            remarkData.append("request", formState.id);
        else if (type === "training")
            remarkData.append("training_request", formState.id);
        else
            remarkData.append("instruction", formState.id);
        const directorRole = contextData?.user?.roles.find((role: string) => role.toLowerCase().includes("director"));
        const feedbackRole = directorRole || contextData?.user?.roles.find((role: string) => FeedbackAllowedRoles.has(role)) || contextData.user?.roles[0];
        remarkData.append("role", getUserType(feedbackRole));
        remarkData.append("remark", formState.remark);

        try {
            let remark_response;
            if (type === "legal")
                remark_response = await createLegalAdvisoryRemark(remarkData);
            else if (type === "sector_directives")
                remark_response = await createSectorDirectiveRemark(remarkData);
            else if (type === "training")
                remark_response = await createLegalTrainingProvisionRemark(remarkData);
            else
                remark_response = await createLegislativeDraftingRemark(remarkData);
            const data = new FormData();
            data.append("current_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.submitted.value);
            let response: any;
            if (type === "legal")
                response = await updateLegalAdvisory(formState.id, data);
            else if (type === "sector_directives")
                response = await updateSectorDirective(formState.id, data);
            else if (type === "training")
                response = await updateLegalTrainingProvision(formState.id, data);
            else
                response = await updateLegislativeDrafting(formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("RequestForwardedSuccess") || "Request forwarded successfully!", "success");
            setEdit(false);
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateRequest") || "Failed to update request.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleForwardToProsecutors = async () => {
        setLoading(true);
        const remarkData = new FormData();
        if (type === "legal")
            remarkData.append("advisory_request", formState.id);
        else if (type === "sector_directives")
            remarkData.append("request", formState.id);
        else if (type === "training")
            remarkData.append("training_request", formState.id);
        else
            remarkData.append("instruction", formState.id);
        const directorRole = contextData?.user?.roles.find((role: string) => role.toLowerCase().includes("director"));
        const feedbackRole = directorRole || contextData?.user?.roles.find((role: string) => FeedbackAllowedRoles.has(role)) || contextData.user?.roles[0];
        remarkData.append("role", getUserType(feedbackRole));
        remarkData.append("remark", formState.remark);

        try {
            let remark_response;
            if (type === "legal")
                remark_response = await createLegalAdvisoryRemark(remarkData);
            else if (type === "sector_directives")
                remark_response = await createSectorDirectiveRemark(remarkData);
            else if (type === "training")
                remark_response = await createLegalTrainingProvisionRemark(remarkData);
            else
                remark_response = await createLegislativeDraftingRemark(remarkData);

            for (const member of teamMembers) {
                const prosecutorData = new FormData();
                if (type === "legal") prosecutorData.append("advisory_request", formState.id);
                else if (type === "sector_directives") prosecutorData.append("request", formState.id);
                else if (type === "legislative") prosecutorData.append("instruction", formState.id);
                else prosecutorData.append("training_request", formState.id);

                prosecutorData.append("prosecutor_name", member.name);
                prosecutorData.append("prosecutor_id", member.id.toString());
                prosecutorData.append(
                    "role",
                    member.isTeamLeader ? "leader" : "member"
                );

                const existingAssignment = formState.prosecutor_assignments?.find((prosecutor: any) => 
                    String(prosecutor.prosecutor_id || prosecutor.prosecutor_user_id || prosecutor.prosecutor) === String(member.id)
                );

                if (existingAssignment && existingAssignment.id) {
                    if (type === "legal") await updateLegalAdvisoryProsecutorAssignment(existingAssignment.id, prosecutorData);
                    else if (type === "sector_directives") await updateSectorDirectiveProsecutorAssignment(existingAssignment.id, prosecutorData);
                    else if (type === "legislative") await updateLegislativeProsecutorAssignment(existingAssignment.id, prosecutorData);
                    else await updateLegalTrainingProvisionProsecutorAssignment(existingAssignment.id, prosecutorData);
                } else {
                    if (type === "legal") await createLegalAdvisoryProsecutorAssignment(prosecutorData);
                    else if (type === "sector_directives") await createSectorDirectiveProsecutorAssignment(prosecutorData);
                    else if (type === "legislative") await createLegislativeProsecutorAssignment(prosecutorData);
                    else await createLegalTrainingProvisionProsecutorAssignment(prosecutorData);
                }
            }

            const data = new FormData();
            let response: any;

            if (type === "training") {
                data.append("status", LEGAL_TRAINING_PROVISION_REQUEST_STATUS.team_assigned.value);
                data.append("forward_status", LEGAL_TRAINING_PROVISION_REQUEST_STATUS.assigned_to_prosecutor.value);
                response = await updateLegalTrainingProvision(formState.id, data);
            } else if (type === "legislative") {
                data.append("current_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.assigned_to_prosecutor.value);
                response = await updateLegislativeDrafting(formState.id, data);
            } else {
                data.append("status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.assigned_to_prosecutor.value);
                if (type === "legal") response = await updateLegalAdvisory(formState.id, data);
                else if (type === "sector_directives") response = await updateSectorDirective(formState.id, data);
                else response = await updateLegalTrainingProvision(formState.id, data);
            }

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("TeamAssignmentsUpdatedSuccess") || "Team assignments updated successfully!", "success");
            setEdit(false);
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateRequest") || "Failed to update request.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveMember = (id: number) => {
        const memberToRemove = teamMembers.find((member) => member.id === id);
        if (
            memberToRemove?.isTeamLeader &&
            teamMembers.filter((m) => m.isTeamLeader).length === 1
        ) {
            setValidationError(
                t("CannotRemoveOnlyTeamLeader") || "Cannot remove the only team leader. Please assign a new team leader first or add another member."
            );
            return;
        }
        setTeamMembers(teamMembers.filter((member) => member.id !== id));
        setValidationError(null);
    };

    const handleToggleTeamLeader = (id: number) => {
        setTeamMembers((prevMembers) =>
            prevMembers.map((member) => {
                if (member.id === id) {
                    return { ...member, isTeamLeader: !member.isTeamLeader };
                } else {
                    return { ...member, isTeamLeader: false };
                }
            })
        );
        setValidationError(null);
    };

    const handleAddProsecutor = () => {
        if (selectedProsecutor) {
            const selectedId = String(selectedProsecutor.id);
            const alreadyAdded = teamMembers.some((member) => String(member.id) === selectedId);
            
            if (!alreadyAdded) {
                setTeamMembers((prevMembers) => [
                    ...prevMembers,
                    {
                        id: selectedProsecutor.id,
                        name: selectedProsecutor.name,
                        role: "Prosecutor",
                        isTeamLeader: prevMembers.length === 0,
                    },
                ]);
                setSelectedProsecutor(null);
                setValidationError(null);
            } else {
                showToast(t("ProsecutorAlreadyAdded") || "This prosecutor is already added to the team.", "error");
            }
        }
    };
    
    return (
        <>
            <div className="mt-6">
                <label
                    htmlFor="remark"
                    className="block text-sm font-medium text-gray-500"
                >
                    {t("Remark") || "Remark"}
                </label>
                <textarea
                    id="remark"
                    name="remark"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm h-24 resize-y"
                    placeholder={t("DetailHere") || "Detail here...."}
                    required
                    value={formState.remark}
                    onChange={handleChange}
                ></textarea>

                {/* Setup Team Members - Same as Coordinator */}
                <div className="mb-6">
                    <label
                        htmlFor="select-prosecutor"
                        className="block text-sm font-medium text-gray-700 mb-2 mt-4"
                    >
                        {t("SetupTeamMembers") || "Setup Team Members"}
                    </label>
                    <div className="relative mb-3">
                        <select
                            id="select-prosecutor"
                            className="appearance-none w-full p-3 border border-gray-300 rounded-md bg-white pr-10 focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                            value={selectedProsecutor ? String(selectedProsecutor.id) : ""}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val) {
                                    const found = employees.find(
                                        (employee: any) => String(employee.id) === val
                                    );
                                    setSelectedProsecutor(found ?? null);
                                } else {
                                    setSelectedProsecutor(null);
                                }
                            }}
                        >
                            <option value="">{t("SelectProsecutor") || "Select Prosecutor"}</option>
                            {employees.map((emp: any) => (
                                <option key={emp.id} value={emp.id}>
                                    {emp.name} - {emp.role}
                                </option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-700">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={handleAddProsecutor}
                        className="mb-4 text-[#AA232F] hover:text-blue-800 text-sm"
                    >
                        {t("AddSelectedProsecutorToTeam") || "Add Selected Prosecutor to Team"}
                    </button>

                    <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar scrollbar-hide">
                        {teamMembers.length === 0 && (
                            <p className="text-gray-500 text-sm italic">
                                {t("NoTeamMembersAddedYet") || "No team members added yet. Please add at least one."}
                            </p>
                        )}
                        {teamMembers.map((member) => (
                            <div
                                key={member.id}
                                className="flex items-center bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm"
                            >
                                <div className="w-10 h-10 rounded-full mr-3 bg-gray-700 text-white flex items-center justify-center font-bold text-sm select-none">
                                    {getInitials(member.name ?? member.prosecutor_name)}
                                </div>

                                <div className="flex-grow">
                                    <p className="font-semibold text-gray-800">
                                        {member.name ?? member.prosecutor_name}
                                    </p>
                                    <p className="text-sm text-gray-500">{member.role ?? "Prosecutor"}</p>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id={`team-leader-${member.id}`}
                                        checked={member.isTeamLeader ?? member.role === "leader"}
                                        onChange={() => handleToggleTeamLeader(member.id)}
                                        className="accent-[#AA232F] form-checkbox h-4 w-4 text-gray-800 border-gray-300 rounded"
                                    />
                                    <label
                                        htmlFor={`team-leader-${member.id}`}
                                        className="text-sm text-gray-700"
                                    >
                                        {t("IsTeamLeader") || "is Team Leader"}
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveMember(member.id)}
                                        className="text-gray-400 hover:text-red-500"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-5 w-5"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="#AA232F"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    {validationError && (
                        <p className="text-red-500 text-sm mt-2">{validationError}</p>
                    )}
                </div>
            </div>
            <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
                <button
                    type="button"
                    className="flex-1 px-6 py-3 rounded-md bg-[#D4D4D4] text-[#212121] font-semibold hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50"
                    onClick={handleReject}
                    disabled={loading}
                >
                    {loading ? (
                        <div className="flex items-center justify-center">
                            {LoadingIcon} {t("Submitting") || "Submitting..."}
                        </div>
                    ) : (
                        t("RespondBack") || "Respond back"
                    )}
                </button>
                <div
                    onClick={() => !loading && handleForwardToCoordinator()}
                    className={`flex-1 text-center cursor-pointer px-6 py-3 rounded-md bg-[#212121] text-white font-semibold hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-600 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    {loading ? (
                        <div className="flex items-center justify-center">
                            {LoadingIcon} {t("Submitting") || "Submitting..."}
                        </div>
                    ) : (
                        t("ForwardToCoordinator") || "Forward To Coordinator"
                    )}
                </div>
                <div
                    onClick={() => !loading && handleForwardToProsecutors()}
                    className={`flex-1 text-center cursor-pointer px-6 py-3 rounded-md bg-[#008000] text-white font-semibold hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    {loading ? (
                        <div className="flex items-center justify-center">
                            {LoadingIcon} {t("Submitting") || "Submitting..."}
                        </div>
                    ) : (
                        t("AssignToProsecutors") || "Assign To Prosecutors"
                    )}
                </div>
            </div>
        </>
    );
}